/* eslint-disable max-lines */
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'common/environments/environment';
import { ApiOptions, ApiOptionsService } from 'common/services/api-options/api-options.service';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';
import { DealerOwner, DistributorOwner, DistributorOwnerPostParam, EnrollmentForm, EnrollmentFormData, EnrollmentFormDetails, OwnerInfo } from 'private/app/models/account-admin-enrollment.model';
import { EnrollmentFormFieldType, FormType } from 'private/app/views/program/enrollment-form/enrollment-form.component';
import { Observable } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import enrolmentStatusValues from 'private/app/views/program/enrollment-form/enrollment-forms/enrollment-status.json';

export interface BrandSold {
    name: string;
    value: string;
}

@Injectable({ providedIn: 'root' })
export class EnrollmentApiService {
    dealerOwnerInfo: OwnerInfo[];
    dealerOwnerAddressOne: string;
    dealerOwnerAddressTwo: string;
    dealerOwnerAddressCity: string;
    dealerOwnerAddressState: string;
    dealerOwnerPostalCode: string;
    dealerOwnerCountry: string;
    distributorManagerFirstName: string;
    distributorManagerLastName: string;
    distributorManagerUserId: number | string;
    distributorManagerCompanyAddressOne: string;
    distributorManagerCompanyAddressTwo: string;
    distributorManagerCity: string;
    distributorManagerState: string;
    distributorManagerPostalCode: string;
    distributorManagerCountry: string;
    distributorSubmittedBy: string;
    private enrollmentFormData: EnrollmentFormDetails;

    constructor(
        private readonly httpClient: HttpClient,
        public translate: TranslateService,
        private readonly apiOptions: ApiOptionsService
    ) {
    }

    setEnrollmentStatus(formValue: UntypedFormGroup, loggedInUserDetails: string, enrollmentStatus: number | null | undefined, ApproverWorkflowStatusId: number | null | undefined) {
        const rawValue = formValue.getRawValue();
        if (loggedInUserDetails === IdmUserRoles.DEFAULT_DISTRIBUTOR) {
            if ((enrollmentStatus === null && ApproverWorkflowStatusId === null) || (enrollmentStatus === enrolmentStatusValues.enrollmentStatus.Returned && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['SM Returned']) || ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['PM Returned']) {
                this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Initial'];
                this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['Dist Submitted'];
            }
        }
        if (loggedInUserDetails === IdmUserRoles.REGIONAL_SALES_MANAGER || loggedInUserDetails === IdmUserRoles.DUCTLESS_RSM) {
            if ((enrollmentStatus === null && ApproverWorkflowStatusId === null)) {
                this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Final'];
                this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['SM Approved'];
            }
            if ((enrollmentStatus === enrolmentStatusValues.enrollmentStatus['Pending Initial'] && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['Dist Submitted']) || (enrollmentStatus === enrolmentStatusValues.enrollmentStatus['Pending Initial'] && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['PM Returned'])) {
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.APPROVE')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Final'];
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['SM Approved'];
                }
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.REJECT')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Initial'];
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['SM Declined'];
                }
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.RETURN')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus.Returned;
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['SM Returned'];
                }
            }
        }
        if (loggedInUserDetails === IdmUserRoles.PROGRAM_MANAGER) {
            if ((enrollmentStatus === null && ApproverWorkflowStatusId === null)) {
                this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus.Active;
                this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['PM Approved'];
            }
            if ((enrollmentStatus === enrolmentStatusValues.enrollmentStatus['Pending Initial'] && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['Dist Submitted']) || (enrollmentStatus === enrolmentStatusValues.enrollmentStatus['Pending Final'] && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['SM Approved'])) {
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.APPROVE')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus.Active;
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['PM Approved'];
                }
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.RETURN')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Initial'];
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['PM Returned'];
                }
            }
            if (enrollmentStatus === enrolmentStatusValues.enrollmentStatus['Pending Initial'] && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['Dist Submitted']) {
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.REJECT')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Initial'];
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['PM Declined'];
                }
            }
            if (enrollmentStatus === enrolmentStatusValues.enrollmentStatus['Pending Final'] && ApproverWorkflowStatusId === enrolmentStatusValues.workflowStatus['SM Approved']) {
                if (rawValue.approverInformation.ApproverAction[0].name === this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.APPROVER_ACTIONS.REJECT')) {
                    this.enrollmentFormData.enrollmentStatus = enrolmentStatusValues.enrollmentStatus['Pending Final'];
                    this.enrollmentFormData.ApproverWorkflowStatusId = enrolmentStatusValues.workflowStatus['PM Declined'];
                }
            }
        }
    }

    createPostBody(formValue: UntypedFormGroup, formdata: EnrollmentForm, formType: string): EnrollmentFormDetails {
        const rawValue = formValue.getRawValue();

        // Dealer info
        this.enrollmentFormData.DealerCompanyId = (rawValue.dealerInformation.DealerCompanyId) ? Number(rawValue.dealerInformation.DealerCompanyId) : null;
        if (formType === FormType.DUCTLESS_WITH_NO_OWNER) {
            this.enrollmentFormData.DealerOwnerId = 0;
        }
        else {
            this.enrollmentFormData.DealerOwnerId = (rawValue.dealerInformation.DealerOwnerId[0].value) ? Number(rawValue.dealerInformation.DealerOwnerId[0].value) : Number(formdata.dealerInformation[6].selectedId);
        }
        this.enrollmentFormData.dealerAddress = (rawValue.dealerInformation.dealerAddress) ? rawValue.dealerInformation.dealerAddress : '';
        this.enrollmentFormData.dealerCompanyName = (rawValue.dealerInformation.dealerCompanyName) ? rawValue.dealerInformation.dealerCompanyName : '';
        this.enrollmentFormData.dealerEmailAddress = (rawValue.dealerInformation.dealerEmailAddress) ? rawValue.dealerInformation.dealerEmailAddress : '';
        this.enrollmentFormData.dealerFax = (rawValue.dealerInformation.dealerFax) ? rawValue.dealerInformation.dealerFax : '';
        this.enrollmentFormData.dealerOwnerEmailAddress = (rawValue.dealerInformation.dealerOwnerEmailAddress) ? rawValue.dealerInformation.dealerOwnerEmailAddress : '';
        this.enrollmentFormData.dealerPhone = (rawValue.dealerInformation.dealerPhone) ? rawValue.dealerInformation.dealerPhone : '';

        // Distributor info
        this.enrollmentFormData.DistributorCompanyId = (rawValue.distributorInformation.DistributorCompanyId) ? Number(rawValue.distributorInformation.DistributorCompanyId) : null;
        this.enrollmentFormData.DistributorContactEmail = (rawValue.distributorInformation.DistributorContactEmail) ? rawValue.distributorInformation.DistributorContactEmail : '';
        this.enrollmentFormData.DistributorSalesManagerId = (rawValue.distributorInformation.DistributorSalesManagerId[0].value) ? Number(rawValue.distributorInformation.DistributorSalesManagerId[0].value) : Number(formdata.distributorInformation[3].selectedId);
        this.enrollmentFormData.distributorSubmittedBy = (rawValue.distributorInformation.distributorSubmittedBy) ? rawValue.distributorInformation.distributorSubmittedBy : '';
        this.enrollmentFormData.DistributorTerritoryManagerId = (rawValue.distributorInformation.DistributorTerritoryManagerId.split('-')[1]) ? Number(rawValue.distributorInformation.DistributorTerritoryManagerId.split('-')[1]) : Number(formdata.distributorInformation[4].selectedId);
        this.enrollmentFormData.distributorAddress = (rawValue.distributorInformation.distributorAddress) ? rawValue.distributorInformation.distributorAddress : '';
        this.enrollmentFormData.distributorName = (rawValue.distributorInformation.distributorName) ? rawValue.distributorInformation.distributorName : '';

        // Percent of Prior Sales
        this.enrollmentFormData.FirstBrandSoldPercentage = (rawValue.percentOfPriorYearSales.FirstBrandSoldPercentage) ? rawValue.percentOfPriorYearSales.FirstBrandSoldPercentage : '';
        this.enrollmentFormData.SecondBrandSoldPercentage = (rawValue.percentOfPriorYearSales.SecondBrandSoldPercentage) ? rawValue.percentOfPriorYearSales.SecondBrandSoldPercentage : '';
        this.enrollmentFormData.ThirdBrandSoldPercentage = (rawValue.percentOfPriorYearSales.ThirdBrandSoldPercentage) ? rawValue.percentOfPriorYearSales.ThirdBrandSoldPercentage : '';
        this.enrollmentFormData.FourthBrandSoldPercentage = (rawValue.percentOfPriorYearSales.FourthBrandSoldPercentage) ? rawValue.percentOfPriorYearSales.FourthBrandSoldPercentage : '';

        // Sales brand sold
        this.enrollmentFormData.FirstBrandSold = this.getBrandSoldValue(rawValue.salesBrandSold.FirstBrandSold);
        this.enrollmentFormData.SecondBrandSold = this.getBrandSoldValue(rawValue.salesBrandSold.SecondBrandSold);
        this.enrollmentFormData.ThirdBrandSold = this.getBrandSoldValue(rawValue.salesBrandSold.ThirdBrandSold);
        this.enrollmentFormData.FourthBrandSold = this.getBrandSoldValue(rawValue.salesBrandSold.FourthBrandSold);

        // sales info common
        this.enrollmentFormData.YearsDealerInProgram = (rawValue.salesInfoCommon.YearsDealerInProgram[0].name) ? rawValue.salesInfoCommon.YearsDealerInProgram[0].name : rawValue.salesInfoCommon.YearsDealerInProgram;
        this.enrollmentFormData.FirstYearFundingForDealer = this.getFundingForDealerValue(rawValue.salesInfoCommon.FirstYearFundingForDealer);
        this.enrollmentFormData.SecondYearFundingForDealer = this.getFundingForDealerValue(rawValue.salesInfoCommon.secondYearFundingForDealer);
        this.enrollmentFormData.ThirdYearFundingForDealer = this.getFundingForDealerValue(rawValue.salesInfoCommon.thirdYearFundingForDealer);

        // sales information
        this.enrollmentFormData.AOREquipmentPurchases = (rawValue.salesInformation.AOREquipmentPurchases) ? rawValue.salesInformation.AOREquipmentPurchases : '';
        this.enrollmentFormData.FirstYearEstimatedPurchases = (rawValue.salesInformation.FirstYearEstimatedPurchases) ? rawValue.salesInformation.FirstYearEstimatedPurchases : '';
        this.enrollmentFormData.SecondYearEstimatedPurchases = (rawValue.salesInformation.SecondYearEstimatedPurchases) ? rawValue.salesInformation.SecondYearEstimatedPurchases : '';
        this.enrollmentFormData.ThirdYearEstimatedPurchases = (rawValue.salesInformation.ThirdYearEstimatedPurchases) ? rawValue.salesInformation.ThirdYearEstimatedPurchases : '';
        this.enrollmentFormData.TotalDealerRevenue = (rawValue.salesInformation.TotalDealerRevenue) ? rawValue.salesInformation.TotalDealerRevenue : '';
        this.enrollmentFormData.AllBrandsTotalDuctlessSalesVolumeCurrentYear = (rawValue.salesInformation.AllBrandsTotalDuctlessSalesVolumeCurrentYear) ? rawValue.salesInformation.AllBrandsTotalDuctlessSalesVolumeCurrentYear : '';
        this.enrollmentFormData.AllBrandsAnnualDuctlessEquipmentPurchasesCurrentYear = (rawValue.salesInformation.AllBrandsAnnualDuctlessEquipmentPurchasesCurrentYear) ? rawValue.salesInformation.AllBrandsAnnualDuctlessEquipmentPurchasesCurrentYear : '';
        this.enrollmentFormData.CBPAnnualDuctlessEquipmentPurchasesCurrentYear = (rawValue.salesInformation.CBPAnnualDuctlessEquipmentPurchasesCurrentYear) ? rawValue.salesInformation.CBPAnnualDuctlessEquipmentPurchasesCurrentYear : '';
        this.enrollmentFormData.AllBrandsDuctlessAORPurchaseVolumeCurrentYear = (rawValue.salesInformation.AllBrandsDuctlessAORPurchaseVolumeCurrentYear) ? rawValue.salesInformation.AllBrandsDuctlessAORPurchaseVolumeCurrentYear : '';
        this.enrollmentFormData.AllBrandsNewConstructionDuctlessPurchasesVolumeCurrentYear = (rawValue.salesInformation.AllBrandsNewConstructionDuctlessPurchasesVolumeCurrentYear) ? rawValue.salesInformation.AllBrandsNewConstructionDuctlessPurchasesVolumeCurrentYear : '';
        this.enrollmentFormData.CompetitiveBrand1NameCurrentYear = (rawValue.salesInformation.CompetitiveBrand1NameCurrentYear) ? rawValue.salesInformation.CompetitiveBrand1NameCurrentYear : '';
        this.enrollmentFormData.CompetitiveBrand1PercentageCurrentYear = (rawValue.salesInformation.CompetitiveBrand1PercentageCurrentYear) ? rawValue.salesInformation.CompetitiveBrand1PercentageCurrentYear : '';
        this.enrollmentFormData.CompetitiveBrand2NameCurrentYear = (rawValue.salesInformation.CompetitiveBrand2NameCurrentYear) ? rawValue.salesInformation.CompetitiveBrand2NameCurrentYear : '';
        this.enrollmentFormData.CompetitiveBrand2PercentageCurrentYear = (rawValue.salesInformation.CompetitiveBrand2PercentageCurrentYear) ? rawValue.salesInformation.CompetitiveBrand2PercentageCurrentYear : '';
        this.enrollmentFormData.CBPBrandPercentageCurrentYear = (rawValue.salesInformation.CBPBrandPercentageCurrentYear) ? rawValue.salesInformation.CBPBrandPercentageCurrentYear : '';

        // split of business
        this.enrollmentFormData.CommercialPercentages = (rawValue.splitOfBusiness.CommercialPercentages) ? rawValue.splitOfBusiness.CommercialPercentages : '';
        this.enrollmentFormData.ResidentialAORPercentage = (rawValue.splitOfBusiness.ResidentialAORPercentage) ? rawValue.splitOfBusiness.ResidentialAORPercentage : '';
        this.enrollmentFormData.ResidentialNewConstructionPercentage = (rawValue.splitOfBusiness.ResidentialNewConstructionPercentage) ? rawValue.splitOfBusiness.ResidentialNewConstructionPercentage : '';

        this.enrollmentFormData.Comment = (rawValue.comments) ? rawValue.comments : '';
        this.enrollmentFormData.ApproverComment = (rawValue.approverInformation.ApproverComments) ? rawValue.approverInformation.ApproverComments : '';

        return this.enrollmentFormData;
    }

    getBrandSoldValue(brandSold: BrandSold[] | string) {
        if (typeof brandSold === 'string') {
            return (brandSold === 'Please Select Brand') ? '' : brandSold;
        }

        return brandSold[0].name;
    }

    getFundingForDealerValue(funding: BrandSold[] | string) {
        return (typeof funding === 'object') ? funding[0].name : funding;
    }

    getEnrollmentFormInfo(companyId: string, pId: string):Observable<EnrollmentFormDetails> {
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options: ApiOptions) => this.httpClient.get<EnrollmentFormDetails>(`${environment.api.accountAdminProgram}/enrollment/form?hvacCompanyId=${companyId}&programId=${pId}`, { ...options })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    getDealerOwners(id: string): Observable<DealerOwner> {
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options: ApiOptions) => this.httpClient.get<DealerOwner>(`${environment.api.accountAdminProgram}/enrollment/dealer/details?hvacCompanyId=${id}`, { ...options })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    getDistributorOwners(distributorOwnerPostParam: DistributorOwnerPostParam): Observable<DistributorOwner[]> {
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options) => this.httpClient.get<DistributorOwner[]>(`${environment.api.accountAdminProgram}/enrollment/distributor/details?hvacCompanyId=${distributorOwnerPostParam.companyId}&programId=${distributorOwnerPostParam.programId}&brandFamily=${distributorOwnerPostParam.brandFamily}`, { ...options })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    postEnrollmentFormData(enrollmentFormDetails: UntypedFormGroup, formdata: EnrollmentForm, formType: string) {
        const options$ = this.apiOptions.getAuthedHttpOptions();

        return options$.pipe(
            switchMap((options: ApiOptions) => this.httpClient.post<EnrollmentFormDetails>(`${environment.api.accountAdminProgram}/enrollment/form`, this.createPostBody(enrollmentFormDetails, formdata, formType), { ...options })),
            catchError((err: HttpErrorResponse) => {
                throw err;
            })
        );
    }

    prepareEnrollmentFormManagers(res: [DealerOwner, DistributorOwner[]], enrollmentForm: EnrollmentForm, data: EnrollmentFormData): EnrollmentForm {
        const [
            dealerOwners,
            distributorManagers
        ] = res;

        const matchedDistributor = distributorManagers?.find((manager) => Number(manager.id) === this.enrollmentFormData.DistributorCompanyId);
        if (dealerOwners) {
            this.dealerOwnerInfo = dealerOwners.ownerInfo;
            this.dealerOwnerAddressOne = dealerOwners.addressOne;
            this.dealerOwnerAddressTwo = dealerOwners.addressTwo;
            this.dealerOwnerAddressCity = dealerOwners.city;
            this.dealerOwnerAddressState = dealerOwners.state;
            this.dealerOwnerPostalCode = dealerOwners.postalCode;
            this.dealerOwnerCountry = dealerOwners.country;
        }
        if (matchedDistributor) {
            this.distributorManagerFirstName = matchedDistributor.firstName;
            this.distributorManagerLastName = matchedDistributor.lastName;
            this.distributorManagerUserId = matchedDistributor.userId;
            this.distributorManagerCompanyAddressOne = matchedDistributor.companyAddressOne;
            this.distributorManagerCompanyAddressTwo = matchedDistributor.companyAddressTwo;
            this.distributorManagerCity = matchedDistributor.city;
            this.distributorManagerState = matchedDistributor.state;
            this.distributorManagerPostalCode = matchedDistributor.postalCode;
            this.distributorManagerCountry = matchedDistributor.country;
        }
        const dealerOwner = enrollmentForm.dealerInformation.filter((dealerField) => dealerField.name === EnrollmentFormFieldType.DEALER_OWNER)[0];
        if (dealerOwner) {
            dealerOwner.options = dealerOwners?.ownerInfo?.map((owner) => ({
                name: owner.ownerName,
                value: `${owner.ownerUserId}`
            }));
            dealerOwner.selectedId = `${data.dealerInformation.DealerOwnerId}`;
        }
        const distributorManagerName = enrollmentForm.distributorInformation.filter((dist) => dist.name === EnrollmentFormFieldType.DISTRIBUTOR_NAME)[0];
        if (distributorManagerName) {
            distributorManagerName.options = distributorManagers?.map((manager) => ({
                name: `${manager.companyName}`,
                value: `${manager.id}`
            }));
            distributorManagerName.selectedId = `${data.distributorInformation.DistributorCompanyId}`;
        }
        const residentialSM = enrollmentForm.distributorInformation.filter((dist) => dist.name === EnrollmentFormFieldType.RESIDENTIAL_SM)[0];
        if (residentialSM) {
            residentialSM.options = matchedDistributor?.salesManagers?.map((manager) => ({
                name: `${manager.firstName} ${manager.lastName}`,
                value: `${manager.userId}`
            }));
            residentialSM.selectedId = `${data.distributorInformation.DistributorSalesManagerId}`;
        }
        const territoryManagers = enrollmentForm.distributorInformation.filter((dist) => dist.name === EnrollmentFormFieldType.TERRITORY_MANAGERS)[0];
        if (territoryManagers) {
            territoryManagers.options = matchedDistributor?.territoryManagers?.map((manager) => ({
                name: `${manager.firstName} ${manager.lastName}`,
                value: `${manager.userId}`
            }));
            territoryManagers.selectedId = `${data.distributorInformation.DistributorTerritoryManagerId}`;
        }

        return enrollmentForm;
    }

    mapEnrollmentResponse(res: EnrollmentFormDetails): EnrollmentFormData {
        this.enrollmentFormData = res;

        return {
            programName: res.programName,
            formType: res.enrollmentFormType.name,
            dealerInformation: {
                dealerCompanyName: res.dealerCompanyName,
                DealerCompanyId: res.DealerCompanyId,
                DealerCompanyIdForUI: res.dealerId,
                dealerAddress: res.dealerAddress,
                dealerPhone: res.dealerPhone,
                dealerFax: res.dealerFax,
                dealerEmailAddress: res.dealerEmailAddress,
                DealerOwnerId: res.DealerOwnerId,
                dealerOwnerEmailAddress: res.dealerOwnerEmailAddress
            },
            distributorInformation: {
                distributorName: res.distributorName,
                DistributorCompanyId: res.DistributorCompanyId,
                DistributorCompanyIdForUI: res.distributorId,
                distributorAddress: res.distributorAddress,
                DistributorSalesManagerId: res.DistributorSalesManagerId,
                DistributorTerritoryManagerId: res.DistributorTerritoryManagerId,
                DistributorContactEmail: res.DistributorContactEmail,
                distributorSubmittedBy: res.distributorSubmittedBy
            },
            salesInfoCommon: {
                YearsDealerInProgram: `${res.YearsDealerInProgram || 1}`,
                FirstYearFundingForDealer: `${res.FirstYearFundingForDealer || ''}`,
                SecondYearFundingForDealer: `${res.SecondYearFundingForDealer || ''}`,
                ThirdYearFundingForDealer: `${res.ThirdYearFundingForDealer || ''}`
            },
            salesInformation: {
                TotalDealerRevenue: `${res.TotalDealerRevenue || ''}`,
                AOREquipmentPurchases: `${res.AOREquipmentPurchases || ''}`,
                FirstYearEstimatedPurchases: `${res.FirstYearEstimatedPurchases || ''}`,
                SecondYearEstimatedPurchases: `${res.SecondYearEstimatedPurchases || ''}`,
                ThirdYearEstimatedPurchases: `${res.ThirdYearEstimatedPurchases || ''}`,
                AllBrandsTotalSalesVolumeCurrentYear: `${res.AllBrandsTotalSalesVolumeCurrentYear || ''}`,
                AllBrandsTotalDuctlessSalesVolumeCurrentYear: `${res.AllBrandsTotalDuctlessSalesVolumeCurrentYear || ''}`,
                AllBrandsAnnualEquipmentPurchasesCurrentYear: `${res.AllBrandsAnnualEquipmentPurchasesCurrentYear || ''}`,
                AllBrandsAnnualDuctlessEquipmentPurchasesCurrentYear: `${res.AllBrandsAnnualDuctlessEquipmentPurchasesCurrentYear || ''}`,
                AllBrandsDuctlessAORPurchaseVolumeCurrentYear: `${res.AllBrandsDuctlessAORPurchaseVolumeCurrentYear || ''}`,
                AllBrandsNewConstructionDuctlessPurchasesVolumeCurrentYear: `${res.AllBrandsNewConstructionDuctlessPurchasesVolumeCurrentYear || ''}`,
                CompetitiveBrand1NameFirstYear: `${res.CompetitiveBrand1NameFirstYear || ''}`,
                CompetitiveBrand1NameCurrentYear: `${res.CompetitiveBrand1NameCurrentYear || ''}`,
                CompetitiveBrand1PercentageFirstYear: `${res.CompetitiveBrand1PercentageFirstYear || ''}`,
                CompetitiveBrand1PercentageCurrentYear: `${res.CompetitiveBrand1PercentageCurrentYear || ''}`,
                CompetitiveBrand2NameFirstYear: `${res.CompetitiveBrand2NameFirstYear || ''}`,
                CompetitiveBrand2NameCurrentYear: `${res.CompetitiveBrand2NameCurrentYear || ''}`,
                CompetitiveBrand2PercentageFirstYear: `${res.CompetitiveBrand2PercentageFirstYear || ''}`,
                CompetitiveBrand2PercentageCurrentYear: `${res.CompetitiveBrand2PercentageCurrentYear || ''}`,
                CBPBrandPercentageCurrentYear: `${res.CBPBrandPercentageCurrentYear || ''}`,
                CBPAnnualDuctlessEquipmentPurchasesCurrentYear: `${res.CBPAnnualDuctlessEquipmentPurchasesCurrentYear || ''}`,
                CBPBrandPercentageFirstYear: `${res.CBPBrandPercentageFirstYear || ''}`,
                CBPBrandPercentageSecondYear: `${res.CBPBrandPercentageSecondYear || ''}`
            },
            splitOfBusiness: {
                ResidentialAORPercentage: `${res.ResidentialAORPercentage || ''}`,
                ResidentialNewConstructionPercentage: `${res.ResidentialNewConstructionPercentage || ''}`,
                CommercialPercentages: `${res.CommercialPercentages || ''}`
            },
            salesBrandSold: {
                FirstBrandSold: `${(res.FirstBrandSold === 'UnSelected' || res.FirstBrandSold === null) ? this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_FORMS.PLEASE_SELECT_BRAND') : res.FirstBrandSold}`,
                SecondBrandSold: `${(res.SecondBrandSold === 'UnSelected' || res.SecondBrandSold === null) ? this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_FORMS.PLEASE_SELECT_BRAND') : res.SecondBrandSold}`,
                ThirdBrandSold: `${(res.ThirdBrandSold === 'UnSelected' || res.ThirdBrandSold === null) ? this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_FORMS.PLEASE_SELECT_BRAND') : res.ThirdBrandSold}`,
                FourthBrandSold: `${(res.FourthBrandSold === 'UnSelected' || res.FourthBrandSold === null) ? this.translate.instant('ACCOUNT_ADMIN.PROGRAMS.ENROLLMENT_FORMS.PLEASE_SELECT_BRAND') : res.FourthBrandSold}`
            },
            percentOfPriorYearSales: {
                FirstBrandSoldPercentage: `${res.FirstBrandSoldPercentage || ''}`,
                SecondBrandSoldPercentage: `${res.SecondBrandSoldPercentage || ''}`,
                ThirdBrandSoldPercentage: `${res.ThirdBrandSoldPercentage || ''}`,
                FourthBrandSoldPercentage: `${res.FourthBrandSoldPercentage || ''}`
            },
            approverInformation: {
                ApproverComments: res.ApproverComment,
                ApproverAction: res.ApproverAction,
                ApproverWorkflowStatusId: res.ApproverWorkflowStatusId,
                approveWorkflowStatusText: res.approveWorkflowStatusText
            },
            distributorComment: res.distributorComment,
            enrollmentStatus: res.enrollmentStatus
        };
    }
}
